import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';

import { planFieldLinkArrival } from '../utils/field-link-arrival.util';
import { FIELD_LINK_SCROLL_PARAM } from '../utils/field-link-navigation.util';
import type { LeveledControl } from '../utils/section-visibility.util';

/** CSS class carrying the 3-second amber pulse. Declared in `styles.css`. */
export const FIELD_LINK_HIGHLIGHT_CLASS = 'highlight-target-field';

/** How long the amber pulse lasts. Must match the `highlightPulse` animation. */
export const FIELD_LINK_HIGHLIGHT_MS = 3000;

/**
 * Qnames already warned about, briefly, across ALL directive instances.
 *
 * Roles 19, 23 and 24 apply this directive per `<table>` and bind the SAME
 * `schema.controls` to each, so a single arrival wakes several instances that
 * each schedule their own reveal. Without this, a target genuinely rendered
 * nowhere would produce one toast per table — five on role 19. Entries clear
 * after the render delay, which is the window in which sibling instances fire.
 */
const warnedQnames = new Set<string>();

/** One tick for Angular to render rows that a section expansion just revealed. */
export const FIELD_LINK_RENDER_DELAY_MS = 100;

/**
 * What the directive needs off a schema control: a qname to match, and a label
 * for the not-found toast.
 *
 * **`ui` is deliberately `unknown`.** Two earlier shapes both failed under
 * `strictTemplates`:
 *  - a bare `{ level?: … }` is a TypeScript **weak type** (every property
 *    optional), so a source must share at least one property with it — and roles
 *    2 and 7 declare a `FormControlUI` with no `level` member at all, since they
 *    render flat, uncollapsible fields. Binding their controls failed with "has
 *    no properties in common".
 *  - intersecting `Record<string, unknown>` to defeat weak-type detection fails
 *    too, because an *interface* never gets an implicit index signature, so
 *    `FormControlUI` is not assignable to it either.
 *
 * The 17 wired sheets declare genuinely heterogeneous `ui` interfaces, and this
 * directive reads exactly one thing from any of them — `ui.level`, and only via
 * `collectAncestorSectionQnames`, where a missing or non-numeric level
 * normalises to `undefined` and is treated as "deeper than any header". Typing
 * the input to a shape none of them share would buy no safety; `unknown` states
 * the real contract, which is that the directive does not interpret `ui` itself.
 * The one place a level IS read casts explicitly — see `attemptArrival`.
 */
export interface FieldLinkTargetControl {
  qname: string;
  label?: string;
  ui: unknown;
}

/**
 * Anything that can accept an ancestor qname. A real `Set<string>` satisfies
 * this structurally, so the sheets holding their expanded sections as a Set
 * pass theirs unchanged; sheets holding a plain record pass a one-line object
 * literal. Declared as a sink rather than as `Set<string>` so no sheet has to
 * fake a Set to satisfy the type — an earlier attempt subclassed Set and
 * overrode only `add()`, leaving `has()`/`size`/iteration reading an empty
 * base Set.
 */
export interface FieldLinkSectionSink {
  add(qname: string): unknown;
}

/**
 * Reveals an arriving field link on a DESTINATION sheet: expand the target row's
 * collapsed ancestor sections, scroll it into view, and flash the amber pulse.
 *
 * ## Why this is a directive and not per-sheet code
 *
 * `ui.linkTo` can name any of 28 role codes, but only three sheets had ever
 * implemented arrival, each by hand, and all three had drifted — different
 * ancestor rules, different not-found behaviour, and one with no highlight CSS at
 * all, which is the defect that prompted this. Applying one directive to a
 * sheet's `<table>` makes arrival a property of being a sheet rather than
 * something a developer must remember to write.
 *
 * ## Usage
 *
 * ```html
 * <table appFieldLinkTarget
 *        fieldLinkSheet="sub-classification"
 *        [fieldLinkControls]="schema.controls"
 *        [fieldLinkSections]="expandedSections"
 *        [fieldLinkIsSectionHeader]="isSectionHeaderFn">
 * ```
 *
 * `fieldLinkControls` and `fieldLinkSheet` are both required in app code —
 * the latter is enforced by `check:field-links`, not by the type. A flat table
 * omits `fieldLinkSections` and gets scroll + highlight only.
 *
 * ## Four decisions that look wrong and are not
 *
 * 1. **It declines an arrival addressed to another sheet** rather than
 *    handling every one it sees — see `ownsArrival`, and the regression that
 *    forced it.
 *
 * 2. **`fieldLinkSections` is MUTATED IN PLACE.** The directive adds ancestor
 *    qnames to the sheet's own `Set` and triggers change detection. The
 *    conventional alternative — emitting an `@Output` the sheet handles — puts
 *    the expansion logic back into every sheet, which is the duplication this
 *    exists to remove. Deliberate; do not "fix" it.
 * 3. **No schema polling.** The sheets this replaces ran a 100ms `setInterval`
 *    waiting for their schema to load. `ngOnChanges` already fires when
 *    `fieldLinkControls` arrives, so a pending target is retried then.
 * 4. **The row query is scoped to the host table**, never `document`. That is what
 *    lets a dual-table sheet apply the directive per table and have each resolve
 *    independently — first match within a table wins.
 *
 * `MessageService` is injected OPTIONALLY: sheets declare it in their own
 * `providers`, but a sheet that does not must not throw.
 */
@Directive({
  selector: '[appFieldLinkTarget]',
  standalone: true,
})
export class FieldLinkTargetDirective implements OnChanges, OnDestroy {
  /** The destination sheet's schema controls, in render order. */
  @Input() fieldLinkControls: readonly FieldLinkTargetControl[] = [];

  /** The sheet's own expanded-sections sink. Mutated in place — see the docblock. */
  @Input() fieldLinkSections?: FieldLinkSectionSink;

  /** The sheet's `isSectionHeader` predicate; leveled DATA rows are not sections. */
  @Input() fieldLinkIsSectionHeader?: (control: FieldLinkTargetControl) => boolean;

  /**
   * This host's OWN sheet, as the `?sheet=` route value (a `SheetType`
   * literal). An arrival naming a different sheet is not ours — see
   * `ownsArrival`.
   *
   * Typed `string`, not `SheetType`, on purpose: every site binds a static
   * attribute (`fieldLinkSheet="sub-classification"`), which Angular passes as
   * a plain `string`, so a `SheetType` input would fail `strictTemplates` at
   * all 23 sites. The value is not left unchecked — `check:field-links`
   * (check 6) asserts every `appFieldLinkTarget` carries this attribute AND
   * that its value equals the sheet the directory actually maps to, which is a
   * stronger guarantee than the type could give: TypeScript can prove the
   * string is *a* sheet name, only the guard can prove it is *this* sheet's.
   */
  @Input() fieldLinkSheet?: string;

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService, { optional: true });

  /** A target read off the URL but not yet resolvable (schema still loading). */
  private pendingQname: string | null = null;

  /** Pending render-delay / highlight-removal timers, cancelled on destroy. */
  private revealTimer: ReturnType<typeof setTimeout> | null = null;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // queryParams replays on subscribe, covering initial load AND same-tab
    // re-navigation from another sheet.
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(() => {
      const target = this.route.snapshot.queryParamMap.get(FIELD_LINK_SCROLL_PARAM);
      if (!target) return;
      if (!this.ownsArrival()) return;

      this.pendingQname = target;
      // Strip the param immediately so a reload cannot re-trigger the scroll.
      // The resulting emission carries no target, so this cannot loop.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { [FIELD_LINK_SCROLL_PARAM]: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.attemptArrival();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The schema loads async; retry a pending target the moment controls arrive.
    if (changes['fieldLinkControls']) this.attemptArrival();
  }

  /**
   * Is this arrival addressed to the sheet this directive is on?
   *
   * **This is the whole reason the directive cannot just take the param.**
   * All 17 rostered sheets carry it, and a field link navigates BETWEEN
   * sheets — but the sheet being left is still mounted when `queryParams`
   * emits, because the container awaits `whenHydrated()` before swapping
   * `currentSheet`. So the source sheet's instance sees the arrival first. If
   * it consumes it, it strips `scrollTo` from the URL, and the destination
   * mounts to a clean URL and does nothing at all — no scroll, no highlight,
   * and (because the source resolves the target in its OWN schema, the two
   * ends sharing a qname on all but three links app-wide) not even a
   * "not found" toast. That was the regression this check exists to prevent.
   *
   * Two permissive cases, both deliberate:
   *  - **no `?sheet=`** — a hand-built deep link. Only one sheet is mounted, so
   *    it is the only candidate owner; refusing would break the link entirely.
   *  - **no `fieldLinkSheet` binding** — the directive stays usable standalone
   *    (its own spec host binds it explicitly). `check:field-links` fails the
   *    build if a real sheet omits it, so this cannot silently regress in app
   *    code.
   */
  private ownsArrival(): boolean {
    const addressee = this.route.snapshot.queryParamMap.get('sheet');
    if (!addressee || !this.fieldLinkSheet) return true;
    return addressee === this.fieldLinkSheet;
  }

  private attemptArrival(): void {
    const qname = this.pendingQname;
    if (!qname || this.fieldLinkControls.length === 0) return;
    this.pendingQname = null;

    // The cast is the counterpart of `ui: unknown` on FieldLinkTargetControl:
    // the ancestor walk reads only `ui.level`, and normalises anything missing
    // or non-numeric to undefined, so it cannot misbehave on a sheet whose `ui`
    // carries no level. The ORIGINAL controls are passed through (not a
    // projection) because the sheet's own `isSectionHeader` predicate inspects
    // fields this directive never models — `type`, `ui.controlType`.
    const plan = planFieldLinkArrival(
      this.fieldLinkControls as readonly LeveledControl[],
      qname,
      this.fieldLinkIsSectionHeader as ((control: LeveledControl) => boolean) | undefined
    );

    if (plan.index === -1) {
      this.warnNotFound(qname, null);
      return;
    }

    if (this.fieldLinkSections) {
      for (const ancestor of plan.ancestors) this.fieldLinkSections.add(ancestor);
      this.cdr.detectChanges();
    }

    const label = this.fieldLinkControls[plan.index]?.label ?? null;
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => {
      this.revealTimer = null;
      this.revealRow(qname, label);
    }, FIELD_LINK_RENDER_DELAY_MS);
  }

  private revealRow(qname: string, label: string | null): void {
    // Query by attribute alone, not `tr[data-qname]`. A destination row is not
    // always a table row — roles 2 and 7 render their schema fields as <div>s
    // inside a <form>. The directive has no business caring about the host tag;
    // what identifies a target is the data-qname attribute.
    const row = this.host.nativeElement.querySelector<HTMLElement>(
      `[data-qname="${qname}"]`
    );

    if (!row) {
      // Another table on this sheet may legitimately own this row. Roles 19, 23
      // and 24 apply this directive per <table> and bind the same
      // `schema.controls` to each, so EVERY instance resolves the target in the
      // schema and schedules a reveal — but only the table that actually renders
      // the row can find it. Scoping the warning to this host would fire one
      // spurious "not found" beside every successful highlight: four of them on
      // role 19. The scroll stays host-scoped; only the WARN decision looks
      // page-wide.
      if (!document.querySelector(`[data-qname="${qname}"]`)) {
        this.warnNotFound(qname, label);
      }
      return;
    }

    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.add(FIELD_LINK_HIGHLIGHT_CLASS);
    if (this.highlightTimer) clearTimeout(this.highlightTimer);
    this.highlightTimer = setTimeout(() => {
      row.classList.remove(FIELD_LINK_HIGHLIGHT_CLASS);
      this.highlightTimer = null;
    }, FIELD_LINK_HIGHLIGHT_MS);
  }

  /**
   * The container destroys a sheet on every navigation, and a field link
   * navigates BETWEEN sheets — so a highlight can easily still be in flight
   * when this directive goes away. Without these, the closures keep the
   * directive and its injected services alive for up to
   * FIELD_LINK_HIGHLIGHT_MS and then touch a detached row.
   */
  ngOnDestroy(): void {
    if (this.revealTimer) clearTimeout(this.revealTimer);
    if (this.highlightTimer) clearTimeout(this.highlightTimer);
  }

  /**
   * Name the field by its LABEL, never its qname. The old copy read
   * `Field "ifrs-full:ProfitLoss" does not exist in this form` — taxonomy jargon
   * shown to a preparer who cannot act on it. With no label resolvable, the copy
   * drops the identifier rather than falling back to the qname.
   */
  private warnNotFound(qname: string, label: string | null): void {
    // Collapse the burst from sibling instances on a multi-table sheet — see
    // `warnedQnames`. One arrival must produce at most one toast.
    if (warnedQnames.has(qname)) return;
    warnedQnames.add(qname);
    setTimeout(() => warnedQnames.delete(qname), FIELD_LINK_RENDER_DELAY_MS);

    this.messageService?.add({
      severity: 'warn',
      summary: 'Linked field not found',
      detail: label
        ? `Could not open "${label}" on this sheet.`
        : 'Could not open the linked field on this sheet.',
    });
  }
}
